import type { Deal, Path, Work } from '../data/types';
import { RESET, liveChallenges, getChallenge } from '../data/exercises';
import type { Challenge } from '../data/types';
import { feedRank, nextGap, type Slot } from '../data/film';
import type { Pencilled } from '../data/film';
import type { PickRef } from './pieces';

/**
 * The person's record. Count-up, never calendar (10 §8): madeDays is the
 * number of days something was made; today's day is madeDays + 1 until
 * something is made today.
 *
 * The copy in localStorage is a cache (16 §4): every save writes through to
 * kindling-users/{uid}.profile, and boot reads that back and merges (see
 * profile-sync.ts), so a cleared browser or a new phone gets the road back.
 * Only `draft` and the per-device flags below never leave the device.
 */

export interface Profile {
  /** the Firebase uid this cache belongs to; a different signed-in uid never inherits it */
  uid?: string;
  /**
   * Whether `uid` was an anonymous identity when this cache was last saved.
   * Missing on caches written before 2026-10-14, which count as "not known to
   * be anonymous" and so are never adopted (identityChange).
   */
  uidAnon?: boolean;
  path: Path;
  madeDays: number;
  /** the intro was seen (front door, Day 1) */
  introSeen?: boolean;
  name?: string;
  /** what they said they would like to be (writer, artist, filmmaker…), from the intro; more than one is allowed and `dream` is the first */
  dreams?: string[];
  dream?: string;
  /**
   * The intro's idea step (the `idea_step` experiment, lib/experiments.ts,
   * well/product/33 §8): what kind of thing they want to make ("a novel", or
   * their own words, at most 40 characters) and what it is about (at most 200).
   * With an `about`, the imagine starter skips the place and the crew draw them
   * inside that idea's world (ideaOf in data/intro-copy).
   */
  dreamKind?: string;
  dreamAbout?: string;
  /** the intro's "When did you last make something, just because?" (recent / ages / never); the dice on Day 2 read it */
  lastMade?: 'recent' | 'ages' | 'never';
  /** the person's moment (R-02) */
  anchor?: 'coffee' | 'commute' | 'lunch' | 'evening' | 'time';
  anchorTime?: string;
  /** ms when the first thing was made; the crew's script is keyed off it */
  startedAt?: number;
  /** the soft save was offered and declined */
  saveDismissed?: boolean;
  /** the reward's "how you get back tomorrow" was shown (components/KeepAsk.tsx): it is asked once */
  keepAsked?: boolean;
  /** the crew's permission answer, mirrored from the user doc */
  crew?: 'on' | 'off' | 'unasked';
  /** YYYY-MM-DD the welcome-back pair was last shown */
  welcomedOn?: string;
  /** drawings on the house, from comebacks (spent in slice 4) */
  giftCredits?: number;
  /** YYYY-MM-DD of the last made day, local time */
  lastMadeOn?: string;
  /** crew who have introduced themselves and been said hello to: id → the count-up day it happened (owner's rule, 2026-09-18: nobody is on the feed before this) */
  met?: Record<string, number>;
  days: Record<number, { challengeId: string; postId?: string; madeOn: string }>;
  /** cards pencilled into the first film's slots (data/film.ts), slot → card */
  pencilled?: Record<string, Pencilled>;
  /** the film table (K33): which piece sits in each slot, and which reels are pinned so a spin leaves them */
  film?: { picks?: Record<string, PickRef>; locked?: string[] };
  /** "Want to be in it?" (well/product/32) was answered "Not now" this many times: once brings it back on a later Home, twice ends it */
  castDeclined?: number;
  /** the drawing of you, once the crew have cast you in your story: Day 2's "Who are you?" then skips the name tag and the photo */
  castMe?: string;
  /** every exercise finished, in order, including repeats and open play (Pages); `days` keeps only one per day */
  made?: { challengeId: string; madeOn: string; postId?: string }[];
  /** what the person was in the middle of, so a reload does not lose it */
  draft?: { challengeId: string; deal: Deal; work: Work; startedAt: number; day: number; fromPostId?: string };
}

const KEY = 'kindling.profile.v2';
const OLD_KEY = 'kindling.profile.v1';

/** Fields that describe this device, not the person; they are never sent to the server. */
const DEVICE_ONLY: (keyof Profile)[] = ['uid', 'uidAnon', 'draft', 'welcomedOn', 'saveDismissed'];

let stampUid: string | undefined;
let stampAnon: boolean | undefined;
let onSave: ((p: Profile) => void) | undefined;

/**
 * The uid every later save is stamped with (set by profile-sync once the user
 * is known), and whether that identity is anonymous. The second is what lets
 * the next boot tell "this phone lost its anonymous sign-in" (keep the road)
 * from "somebody else's account was here" (never inherit it): identityChange.
 */
export function setProfileUid(uid: string | undefined, anonymous?: boolean) { stampUid = uid; stampAnon = uid ? anonymous : undefined; }
/** The uid saves are being stamped with now, if boot has claimed one. */
export function profileUid(): string | undefined { return stampUid; }
/** Called after every save; profile-sync uses it to write through to the server. */
export function onProfileSave(fn: ((p: Profile) => void) | undefined) { onSave = fn; }

export function clearProfile() {
  try { localStorage.removeItem(KEY); localStorage.removeItem(OLD_KEY); } catch { /* ignore */ }
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function loadProfile(): Profile {
  try {
    // v1 had no uid stamp; read it once and let the next save write it back as v2
    const raw = localStorage.getItem(KEY) ?? localStorage.getItem(OLD_KEY);
    if (raw) return { path: 'writing', madeDays: 0, days: {}, ...JSON.parse(raw) };
  } catch { /* private mode or blocked storage: run from memory */ }
  return { path: 'writing', madeDays: 0, days: {} };
}

export function saveProfile(p: Profile) {
  // uidAnon is rewritten with the uid every time, never carried over from the
  // cache: an old `true` riding along after the account was saved to Google
  // would let a later boot hand a saved account's road to a stranger.
  const next = stampUid ? { ...p, uid: stampUid, uidAnon: stampAnon } : p;
  try { localStorage.setItem(KEY, JSON.stringify(next)); localStorage.removeItem(OLD_KEY); } catch { /* ignore */ }
  onSave?.(next);
}

/**
 * What boot does with a cache when the signed-in identity is not the one it was
 * stamped with (profile-sync).
 *
 * - `same`: the cache is this identity's.
 * - `unclaimed`: the cache was never stamped (somebody new, whose first taps
 *   landed before boot signed them in). It is theirs.
 * - `adopt`: an anonymous identity replaced by another anonymous one. Nobody
 *   signed in or out: there is no button that does either anonymously. What
 *   happened is that the phone lost the old sign-in while keeping
 *   localStorage, which Facebook's and Instagram's in-app browsers do
 *   (2026-10-14: same PostHog id, same cache, a second anonymous uid 2.5
 *   minutes after the first, and the intro shown again). Same device, same
 *   person: the road moves to the new uid.
 * - `clear`: a real account is on one side of the change. Signing into an
 *   account that already has a feed, signing out of one, or a saved account's
 *   sign-in going missing: a cache stamped with another uid is somebody else's
 *   road and is never inherited. The account's own copy comes back from the
 *   server on sign-in, so this costs a saved person nothing; adopting would
 *   hand a saved person's road to whoever picks up a shared phone. A cache
 *   from before uidAnon existed counts as an account here: unknown is not
 *   anonymous.
 */
export type IdentityChange = 'same' | 'unclaimed' | 'adopt' | 'clear';
export function identityChange(cache: Pick<Profile, 'uid' | 'uidAnon'>, user: { uid: string; isAnonymous: boolean }): IdentityChange {
  if (!cache.uid) return 'unclaimed';
  if (cache.uid === user.uid) return 'same';
  return cache.uidAnon === true && user.isAnonymous ? 'adopt' : 'clear';
}

/** The part of a profile that belongs to the person, for kindling-users/{uid}.profile. */
export function toServerProfile(p: Profile): Partial<Profile> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) if (!DEVICE_ONLY.includes(k as keyof Profile) && v !== undefined) out[k] = stripUndefined(v);
  return out as Partial<Profile>;
}

/** Firestore rejects an `undefined` anywhere in the document, however deep. Drop those keys. */
function stripUndefined<T>(v: T): T {
  if (Array.isArray(v)) return v.map(stripUndefined) as unknown as T;
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) if (x !== undefined) out[k] = stripUndefined(x);
    return out as T;
  }
  return v;
}

/**
 * The cache and the server copy, reconciled (16 §4). Whichever has made more
 * days wins; on a tie the server does, since it is what every device sees.
 * Days and crew met are unioned either way, the earliest start is kept, and
 * the device-only fields (the draft, the welcome flags) always come from the
 * cache.
 */
export function mergeProfiles(local: Profile, server: Partial<Profile> | null): Profile {
  if (!server) return local;
  const s: Profile = { path: 'writing', madeDays: 0, days: {}, ...server };
  const [winner, loser] = local.madeDays > s.madeDays ? [local, s] : [s, local];
  const made = (winner.made?.length ?? 0) >= (loser.made?.length ?? 0) ? winner.made : loser.made;
  const starts = [local.startedAt, s.startedAt].filter((x): x is number => typeof x === 'number');
  const out: Profile = {
    ...loser, ...winner,
    days: { ...loser.days, ...winner.days },
    met: (winner.met || loser.met) ? { ...(loser.met || {}), ...(winner.met || {}) } : undefined,
    made,
    startedAt: starts.length ? Math.min(...starts) : undefined,
    introSeen: local.introSeen || s.introSeen || undefined,
  };
  for (const k of DEVICE_ONLY) (out as unknown as Record<string, unknown>)[k] = local[k];
  return JSON.parse(JSON.stringify(out));
}

/**
 * The crew's answer as the account holds it (kindling-users/{uid}.crew, which
 * only the permission route writes) wins over the device's copy. Permission is
 * asked once per account, not once per device: without this, a new phone, a
 * cleared cache or a profile write-through that never landed put the question
 * back in front of somebody who had already answered it.
 */
export function withAccountCrew(p: Profile, accountCrew: unknown): Profile {
  return accountCrew === 'on' || accountCrew === 'off' ? { ...p, crew: accountCrew } : p;
}

/** The day number the next finished exercise counts as. */
export function currentDay(p: Profile): number {
  return p.lastMadeOn === today() ? p.madeDays : p.madeDays + 1;
}

export function madeToday(p: Profile): boolean {
  return p.lastMadeOn === today();
}

export function recordMade(p: Profile, day: number, challengeId: string, postId?: string): Profile {
  const made = [...(p.made || [])];
  const last = made[made.length - 1];
  // the same finish is recorded twice (render, then keep with the postId): update, don't duplicate
  // an explicit `postId: undefined` is not a field Firestore will take, and the
  // first record of a finish has no post yet: leave the key off instead
  const withPost = postId ? { postId } : {};
  if (last && last.challengeId === challengeId && last.madeOn === today() && !last.postId) made[made.length - 1] = { ...last, ...withPost };
  else made.push({ challengeId, madeOn: today(), ...withPost });
  const next: Profile = { ...p, days: { ...p.days, [day]: { challengeId, madeOn: today(), ...withPost } }, made, draft: undefined };
  if (!madeToday(p)) { next.madeDays = day; next.lastMadeOn = today(); }
  if (!next.startedAt) next.startedAt = Date.now();
  saveProfile(next);
  return next;
}

/** Hello said back to a crew member; from now on they are on the feed. */
export function recordMet(p: Profile, crewId: string): Profile {
  const next: Profile = { ...p, met: { ...(p.met || {}), [crewId]: p.madeDays } };
  saveProfile(next);
  return next;
}

/** Every exercise id the person has finished at least once (days, plus repeats and open play). */
export function doneIds(p: Profile): Set<string> {
  const ids = new Set<string>();
  for (const d of Object.values(p.days)) ids.add(d.challengeId);
  for (const m of p.made || []) ids.add(m.challengeId);
  return ids;
}

/** Imagine Yourself There, the starter whose print Put Yourself In It draws again. */
export const IMAGINE_ID = 'imagine-yourself-there';
/** Put Yourself In It (screens/PutMeIn.tsx): the photo, asked after the imagine print instead of before it. */
export const PUT_ME_IN_ID = 'put-yourself-in-it';

/** The imagine print's post, the one a photo is put into; none before it was kept. */
export function imaginePostId(p: Partial<Profile>): string | undefined {
  return [...(p.made || [])].reverse().find((m) => m.challengeId === IMAGINE_ID && m.postId)?.postId
    ?? Object.values(p.days || {}).find((d) => d.challengeId === IMAGINE_ID && d.postId)?.postId;
}

/**
 * Whether Put Yourself In It is on the road (owner, 2026-10-30): after the
 * imagine print, as its first stop, until it is done. Not for someone the crew
 * have drawn already (castMe, from "Be in your story"), nor with the crew off,
 * when nothing is drawn from a photo.
 */
export function putMeInOnRoad(p: Partial<Profile>): boolean {
  if ((p.made || []).some((m) => m.challengeId === PUT_ME_IN_ID)) return true;
  return !!imaginePostId(p) && !p.castMe && p.crew !== 'off';
}

/** The road's exercises in order: the reset, led by Put Yourself In It when it is due or done. */
export function roadFor(p: Pick<Profile, 'path'> & Partial<Profile>): string[] {
  return putMeInOnRoad(p) ? [PUT_ME_IN_ID, ...RESET[p.path]] : RESET[p.path];
}

/**
 * Put Yourself In It is done: it counts as made (so the road's stop is ticked)
 * without taking a day's place, which stays the imagine print's. `me` is the
 * drawing of them, which "Who are you?" then uses instead of asking again.
 */
export function recordPutMeIn(p: Profile, postId: string, me: string): Profile {
  const next: Profile = { ...p, made: [...(p.made || []), { challengeId: PUT_ME_IN_ID, madeOn: today(), postId }], castMe: me };
  saveProfile(next);
  return next;
}

/**
 * The next exercise on the person's road: the first of the reset they have not
 * done. Doing one off-path or again does not move the road; once the reset is
 * finished it deals by day, wrapping.
 */
export function nextOnPath(p: Profile): Challenge {
  const done = doneIds(p);
  const seq = roadFor(p);
  const id = seq.find((x) => !done.has(x)) ?? seq[(Math.max(1, currentDay(p)) - 1) % seq.length];
  return getChallenge(id)!;
}

/** Open play: any exercise from the whole deck, not today's, never one done today; favouring the ones not tried yet. */
export function dealAny(p: Profile, exclude: string[] = []): Challenge {
  const done = doneIds(p);
  const skip = new Set([...exclude, ...(p.made || []).filter((m) => m.madeOn === today()).map((m) => m.challengeId)]);
  const pool = liveChallenges().filter((c) => c.purpose !== 'open' && !skip.has(c.id));
  const fresh = pool.filter((c) => !done.has(c.id));
  const from = fresh.length ? fresh : pool;
  return from[Math.floor(Math.random() * from.length)];
}

/**
 * DEV only: `/?day=N` writes a profile that has made N days (yesterday last; `&away=D` puts the last one D days ago),
 * so any later section can be opened cold: the feed on Day 9, a return after
 * three days (`&away=3`), the crew already met (`&met=all`).
 */
export function devProfileForDay(params: URLSearchParams): Profile | null {
  if (!import.meta.env.DEV || !params.has('day')) return null;
  const n = Math.max(0, Math.min(60, Number(params.get('day')) || 0));
  const away = Math.max(1, Number(params.get('away')) || 1);
  const seq = RESET.writing;
  const at = (k: number) => { const d = new Date(); d.setDate(d.getDate() - (n - k) - away); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const days: Profile['days'] = {};
  for (let k = 1; k <= n; k++) days[k] = { challengeId: seq[(k - 1) % seq.length], madeOn: at(k) };
  const p: Profile = { path: 'writing', madeDays: n, days, introSeen: true, crew: 'on', anchor: 'coffee', startedAt: Date.now() - (n + away - 1) * 86_400_000, lastMadeOn: n ? at(n) : undefined };
  if (params.get('met') === 'all') p.met = { kraben: 2, 'mrs-jennys': 3, 'the-moon': 5, 'the-censor': 9 };
  saveProfile(p);
  return p;
}

/** Whole days since something was last made; 0 today, large when never. */
export function daysSinceMade(p: Profile): number {
  if (!p.lastMadeOn) return 999;
  const [y, m, d] = p.lastMadeOn.split('-').map(Number);
  const last = new Date(y, m - 1, d).getTime();
  const now = new Date(); const t = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((t - last) / 86_400_000);
}

/**
 * What Home offers (K34 N-03 over N-05, core loop doc 20 §5): the exercises
 * that feed the film's next gap, big; then a few others; "Deal me anything"
 * is drawn by the screen. `filled` is the set of slots the film has.
 *
 * For the gap: the road's next exercise first if it feeds the gap, then the
 * ones that fill it squarely before the ones that give it in passing (feedRank),
 * then ones not yet done before ones done before, then the shortest; never one
 * done today. Retired exercises are never offered. Others: the
 * road's next (if it was not in the first group), then fresh ones from the
 * deck, so the road is always one tap away without being the only tap.
 */
export function tilesFor(p: Profile, filled: Set<string>, opts: { forGap?: number; others?: number } = {}): { gap: Slot | null; forGap: Challenge[]; others: Challenge[] } {
  const gap = nextGap(filled);
  const done = doneIds(p);
  const today = new Set((p.made || []).filter((m) => m.madeOn === todayStr()).map((m) => m.challengeId));
  const next = nextOnPath(p);
  const pool = liveChallenges().filter((c) => c.purpose !== 'open' && !c.input.private && !today.has(c.id));
  const rank = (c: Challenge) => (c.id === next.id ? 0 : done.has(c.id) ? 2 : 1);
  const lead = (c: Challenge) => (c.id === next.id ? 0 : 1);
  const feeds = gap ? pool.filter((c) => feedRank(c, gap) !== null).sort((a, b) => lead(a) - lead(b) || feedRank(a, gap)! - feedRank(b, gap)! || rank(a) - rank(b) || a.minutes - b.minutes) : [];
  const forGap = feeds.slice(0, opts.forGap ?? 4);
  const taken = new Set(forGap.map((c) => c.id));
  const rest = pool.filter((c) => !taken.has(c.id)).sort((a, b) => rank(a) - rank(b) || a.minutes - b.minutes);
  return { gap, forGap, others: rest.slice(0, opts.others ?? 3) };
}

function todayStr(): string { return today(); }

